<template>
  <div class="observability">
    <div class="observability-header">
      <h2>Observability</h2>
      <div class="actions">
        <span v-if="lastRefreshedAt" class="last-refreshed">Updated {{ formatRelative(lastRefreshedAt) }}</span>
        <p-button :disabled="isRefreshing" variant="text" color="primary" @click="refresh">{{ isRefreshing ? 'Refreshing…' : 'Refresh' }}</p-button>
      </div>
    </div>

    <div class="cards">
      <section class="card">
        <header class="card-header">
          <h3>
            Scheduler
            <span
              class="help-icon"
              data-tip="Background job that periodically connects to Microsoft 365 and pulls new emails for all monitored mailboxes. Shows the cron schedule, when it last ran, how long it took, and when the next run is due. If 'Last error' is filled in, the most recent pull failed."
            ><p-icon name="help" /></span>
          </h3>
          <span
            v-if="schedulerStatus"
            :class="['badge', schedulerStatus.inProgress ? 'badge-running' : 'badge-idle']"
          >{{ schedulerStatus.inProgress ? 'Running' : 'Idle' }}</span>
        </header>
        <div class="card-body">
          <div v-if="isSchedulerStatusFailed" class="error">Failed to load scheduler status.</div>
          <div v-else-if="!schedulerStatus" class="muted">Loading…</div>
          <template v-else>
            <div class="kv"><span>Cron</span><span class="mono">{{ schedulerStatus.cron }}</span></div>
            <div class="kv"><span>Last run</span><span class="mono">{{ formatDate(schedulerStatus.lastRunAt) }}</span></div>
            <div class="kv"><span>Last finished</span><span class="mono">{{ formatDate(schedulerStatus.lastFinishedAt) }}</span></div>
            <div class="kv"><span>Duration</span><span class="mono">{{ formatDuration(schedulerStatus.lastDurationMs) }}</span></div>
            <div class="kv"><span>Next run</span><span class="mono">{{ formatDate(schedulerStatus.nextRunAt) }}</span></div>
            <div v-if="schedulerStatus.lastError" class="kv kv-error">
              <span>Last error</span><span class="mono">{{ schedulerStatus.lastError }}</span>
            </div>
          </template>
        </div>
      </section>

      <section class="card">
        <header class="card-header">
          <h3>
            Queue (downstream)
            <span
              class="help-icon"
              data-tip="Internal processing queues AFTER an email is pulled from Microsoft. Received: emails waiting to be processed. Migrated: emails waiting to be attached to inventions. Dead-letter: emails that repeatedly failed and need manual review. 'Backlog' is the total of items still to do. The 'by mailbox' breakdown shows which user's emails are currently being processed (based on a sample of up to 500 jobs)."
            ><p-icon name="help" /></span>
          </h3>
          <span v-if="queueStats" class="badge badge-total">backlog: {{ queueStats.totalBacklog }}</span>
        </header>
        <div class="card-body">
          <div v-if="isQueueStatsFailed" class="error">Failed to load queue stats.</div>
          <div v-else-if="!queueStats" class="muted">Loading…</div>
          <template v-else>
            <div v-for="block in queueBlocks" :key="block.title" class="queue-block">
              <div class="queue-title">{{ block.title }}</div>
              <div class="queue-counts">
                <span v-for="(value, key) in block.counts" :key="block.title + '-' + key" class="count">
                  <span class="count-label">{{ key }}</span><span class="count-value">{{ value }}</span>
                </span>
              </div>
              <div v-if="block.breakdown && block.breakdown.byMailbox.length" class="breakdown">
                <div class="breakdown-title">by mailbox (sampled {{ block.breakdown.size }})</div>
                <ul class="breakdown-list">
                  <li
                    v-for="row in block.breakdown.byMailbox"
                    :key="block.title + '-bd-' + row.mailbox"
                    class="breakdown-row"
                  >
                    <div class="breakdown-mailbox" :title="row.mailbox">{{ row.mailbox }}</div>
                    <div class="breakdown-count mono">{{ row.count }}</div>
                    <div class="breakdown-percent mono">{{ row.percent }}%</div>
                    <div class="breakdown-bar-col">
                      <div class="breakdown-bar" :style="{ width: row.percent + '%' }"></div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>

    <section class="card sync-card">
      <header class="card-header">
        <h3>
          Per-mailbox sync state
          <span
            class="help-icon"
            data-tip="One row per monitored mailbox (aggregated across all its folders). Folders = how many folder cursors we track. Last sync = the most recent moment we asked Microsoft for changes anywhere in this mailbox (success or failure) — use it to spot mailboxes that have stalled. Last success = the most recent moment we got a clean response — the gap between Last sync and Last success is how long the mailbox has been failing. Msg Events = Microsoft Graph delta message events pulled across all folders since each cursor was last reset. A single email can produce more than one event when its Graph metadata changes, for example after it is marked as downloaded. Errors = how many folders are currently failing; red rows mean at least one folder is in error. Last error = the most recent failure message in this mailbox."
          ><p-icon name="help" /></span>
        </h3>
        <span class="badge badge-total">{{ syncStatus.length }} cursor{{ syncStatus.length === 1 ? '' : 's' }} · {{ mailboxCount }} mailbox{{ mailboxCount === 1 ? '' : 'es' }}</span>
      </header>
      <div class="card-body card-body-flush">
        <div v-if="isSyncStatusFailed" class="error pad">Failed to load sync status.</div>
        <div v-else-if="!syncStatus.length" class="muted pad">No cursor data yet.</div>
        <ul v-else class="sync-list">
          <li class="sync-item header">
            <div>Mailbox</div>
            <div class="num">Folders</div>
            <div>Last sync</div>
            <div>Last success</div>
            <div class="num">Msg Events</div>
            <div class="num">Errors</div>
            <div :title="'Most recent error while pulling emails from Microsoft Graph (auth, throttling, network, invalid delta link). Cleared on the next successful pull. Does NOT show errors that happen later, when an email is processed by hub-correspondence — those live in the Dead-letter queue below.'">Last pull error (Graph)</div>
          </li>
          <li
            v-for="row in aggregatedSyncStatus"
            :key="row.key"
            class="sync-item"
            :class="{ 'is-error': row.errorCount > 0 }"
          >
            <div class="cell-mailbox" :title="row.userMail || row.userId || ''">{{ row.userMail || row.userId || '—' }}</div>
            <div class="num mono">{{ row.folderCount }}</div>
            <div class="mono">{{ formatDateShort(row.lastSyncedAt) }}</div>
            <div class="mono">{{ formatDateShort(row.lastSuccessAt) }}</div>
            <div class="num mono">{{ row.messagesProcessed }}</div>
            <div class="num mono">{{ row.errorCount }}</div>
            <div class="cell-error" :title="row.lastError || ''">{{ row.lastError || '' }}</div>
          </li>
        </ul>
      </div>
    </section>

    <section class="card sync-card">
      <header class="card-header">
        <h3>
          Dead-letter queue
          <span
            class="help-icon help-icon--up"
            data-tip="Emails that failed processing in hub-correspondence after all retry attempts (5 by default). They sit in Redis untouched and require manual investigation. Each row shows which mailbox the email belongs to, when it was sent/received, the subject, how many attachments it had, the number of attempts made before giving up, and the error message that caused the final failure. Use 'Refresh' or wait for the auto-refresh (every 15s)."
          ><p-icon name="help" /></span>
        </h3>
        <span class="badge badge-total">{{ deadLetter.total }} item{{ deadLetter.total === 1 ? '' : 's' }}</span>
      </header>
      <div class="card-body card-body-flush">
        <div v-if="isDeadLetterFailed" class="error pad">Failed to load dead-letter queue.</div>
        <div v-else-if="isDeadLetterPending && !deadLetter.items.length" class="muted pad">Loading…</div>
        <div v-else-if="!deadLetter.items.length" class="muted pad">Dead-letter queue is empty.</div>
        <ul v-else class="dl-list">
          <li class="dl-item header">
            <div>Mailbox</div>
            <div>Added</div>
            <div>Email date</div>
            <div>Subject</div>
            <div class="num">Attach.</div>
            <div class="num">Attempts</div>
            <div :title="'Reason the email could not be processed by hub-correspondence after it was already pulled from Microsoft Graph (e.g. MongoDB unavailable, storage error, parsing failure). These are separate from the Graph pull errors shown above.'">Processing error (hub)</div>
            <div class="cell-action">Actions</div>
          </li>
          <li
            v-for="row in deadLetter.items"
            :key="'dl-' + row.jobId"
            class="dl-item is-error"
          >
            <div class="cell-mailbox" :title="row.mailbox || ''">{{ row.mailbox || '—' }}</div>
            <div class="mono">{{ formatDateShort(row.addedAt) }}</div>
            <div class="mono">{{ formatDateShort(row.emailDate) }}</div>
            <div class="cell-subject" :title="row.subject || ''">{{ row.subject || '—' }}</div>
            <div class="num mono">{{ row.attachmentsCount }}</div>
            <div class="num mono">{{ row.attemptsMade != null ? row.attemptsMade : '—' }}</div>
            <div class="cell-error" :title="row.error || ''">{{ row.error || '—' }}</div>
            <div class="cell-action">
              <p-button
                variant="text"
                color="primary"
                title="Re-enqueue this email for processing"
                :disabled="busyDeadLetterId === row.jobId"
                @click="retryDead(row)"
              >&#8634;</p-button>
              <p-button
                variant="text"
                color="primary"
                title="Remove from dead-letter queue"
                :disabled="busyDeadLetterId === row.jobId"
                @click="removeDead(row)"
              >&#10005;</p-button>
            </div>
          </li>
        </ul>
        <div v-if="deadLetter.items.length || deadLetter.total > 0" class="pagination">
          <p-button
            variant="text"
            :disabled="deadLetter.page <= 1 || isDeadLetterPending"
            @click="goToDeadLetterPage(deadLetter.page - 1)"
          >&#8249;</p-button>
          <span>{{ deadLetter.page }} / {{ deadLetterTotalPages }}</span>
          <p-button
            variant="text"
            :disabled="deadLetter.page >= deadLetterTotalPages || isDeadLetterPending"
            @click="goToDeadLetterPage(deadLetter.page + 1)"
          >&#8250;</p-button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import format from 'date-fns/fp/format/index.js';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import Button from '@/components/common/Button';
import Icon from '@/components/common/Icon';

const REFRESH_INTERVAL_MS = 15000;

export default {
  components: {
    'p-button': Button,
    'p-icon': Icon
  },
  data() {
    return {
      timer: null,
      busyDeadLetterId: null
    };
  },
  computed: {
    ...mapState('emailsObservability', [
      'syncStatus',
      'isSyncStatusPending',
      'isSyncStatusFailed',
      'schedulerStatus',
      'isSchedulerStatusPending',
      'isSchedulerStatusFailed',
      'queueStats',
      'isQueueStatsPending',
      'isQueueStatsFailed',
      'deadLetter',
      'isDeadLetterPending',
      'isDeadLetterFailed',
      'lastRefreshedAt'
    ]),
    isRefreshing() {
      return this.isSyncStatusPending || this.isSchedulerStatusPending || this.isQueueStatsPending || this.isDeadLetterPending;
    },
    queueBlocks() {
      if (!this.queueStats) return [];
      const breakdown = this.queueStats.breakdown || {};
      return [
        { title: 'received',    counts: this.queueStats.received    || {}, breakdown: breakdown.received    || null },
        { title: 'migrated',    counts: this.queueStats.migrated    || {}, breakdown: breakdown.migrated    || null },
        { title: 'dead-letter', counts: this.queueStats.deadLetter  || {}, breakdown: null }
      ];
    },
    aggregatedSyncStatus() {
      const maxDate = (a, b) => {
        if (!a) return b;
        if (!b) return a;
        return new Date(a) > new Date(b) ? a : b;
      };
      const groups = new Map();
      for (const row of this.syncStatus) {
        const key = (row.userMail || row.userId || '').toLowerCase() || '(unknown)';
        let agg = groups.get(key);
        if (!agg) {
          agg = {
            key,
            userMail: row.userMail,
            userId: row.userId,
            folderCount: 0,
            messagesProcessed: 0,
            errorCount: 0,
            lastSyncedAt: null,
            lastSuccessAt: null,
            lastErrorAt: null,
            lastError: null
          };
          groups.set(key, agg);
        }
        agg.folderCount += 1;
        agg.messagesProcessed += row.messagesProcessed || 0;
        if (row.lastError) {
          agg.errorCount += 1;
          const candidate = row.lastSyncedAt;
          if (!agg.lastErrorAt || (candidate && new Date(candidate) > new Date(agg.lastErrorAt))) {
            agg.lastErrorAt = candidate;
            agg.lastError = row.lastError;
          }
        }
        agg.lastSyncedAt = maxDate(agg.lastSyncedAt, row.lastSyncedAt);
        agg.lastSuccessAt = maxDate(agg.lastSuccessAt, row.lastSuccessAt);
      }
      return Array.from(groups.values()).sort((a, b) => {
        if (a.errorCount !== b.errorCount) return b.errorCount - a.errorCount;
        return (a.userMail || a.userId || '').localeCompare(b.userMail || b.userId || '');
      });
    },
    mailboxCount() {
      const set = new Set();
      for (const row of this.syncStatus) {
        set.add((row.userMail || row.userId || '').toLowerCase());
      }
      return set.size;
    },
    deadLetterTotalPages() {
      const size = this.deadLetter.size || 20;
      const total = this.deadLetter.total || 0;
      return Math.max(1, Math.ceil(total / size));
    }
  },
  async mounted() {
    await this.refresh();
    this.timer = setInterval(() => this.refresh(), REFRESH_INTERVAL_MS);
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  },
  methods: {
    ...mapActions('emailsObservability', [
      'refreshAll',
      'getDeadLetter',
      'retryDeadLetter',
      'removeDeadLetter'
    ]),
    async refresh() {
      if (this.isRefreshing) {
        return;
      }
      await this.refreshAll();
    },
    async goToDeadLetterPage(page) {
      if (page < 1 || page > this.deadLetterTotalPages) return;
      await this.getDeadLetter({ page, size: this.deadLetter.size });
    },
    async retryDead(row) {
      if (this.busyDeadLetterId) return;
      this.busyDeadLetterId = row.jobId;
      try {
        await this.retryDeadLetter(row.jobId);
      } catch {
        this.$toast?.error({ title: 'Failed to retry email' });
      } finally {
        this.busyDeadLetterId = null;
      }
    },
    async removeDead(row) {
      if (this.busyDeadLetterId) return;
      const confirmed = await this.$confirm({
        title: 'Remove from dead-letter queue',
        message: `Remove email "${row.subject || row.jobId}" from the dead-letter queue?\n\nThis cannot be undone.`,
        confirm: 'Remove'
      });
      if (!confirmed) return;
      this.busyDeadLetterId = row.jobId;
      try {
        await this.removeDeadLetter(row.jobId);
      } catch {
        this.$toast?.error({ title: 'Failed to remove email' });
      } finally {
        this.busyDeadLetterId = null;
      }
    },
    formatDate(value) {
      if (!value) return '—';
      try {
        return format('yyyy-MM-dd HH:mm:ss', new Date(value));
      } catch {
        return '—';
      }
    },
    formatDateShort(value) {
      if (!value) return '—';
      try {
        return format('yyyy-MM-dd HH:mm', new Date(value));
      } catch {
        return '—';
      }
    },
    formatRelative(value) {
      if (!value) return '';
      try {
        return formatDistanceToNow(new Date(value), { addSuffix: true });
      } catch {
        return '';
      }
    },
    formatDuration(ms) {
      if (ms == null) return '—';
      if (ms < 1000) return `${ms} ms`;
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) return `${seconds} s`;
      const minutes = Math.floor(seconds / 60);
      const remSeconds = seconds % 60;
      return `${minutes}m ${remSeconds}s`;
    }
  }
};
</script>

<style lang="scss" scoped>
.observability {
  margin-top: 2rem;
  margin-bottom: 3rem;
  width: 75%;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .observability-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    h2 { margin: 0; }

    .actions {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .last-refreshed {
      font-size: 0.75rem;
      color: var(--theme-on-background-accent);
    }
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1rem;
  }

  .card {
    border: 1px solid var(--theme-highlight);
    border-radius: 4px;
    background: var(--theme-surface);
    display: flex;
    flex-direction: column;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--theme-highlight);
      background: var(--theme-highlight);

      h3 {
        margin: 0;
        font-size: 0.85rem;
        font-weight: 700;
        color: var(--theme-on-background-accent);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
      }

      .help-icon {
        cursor: help;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--theme-on-background-accent);
        opacity: 0.7;
        transition: opacity 0.15s, color 0.15s;

        &:hover {
          opacity: 1;
          color: var(--theme-primary);
        }

        &::after {
          content: attr(data-tip);
          position: absolute;
          top: calc(100% + 10px);
          left: 0;
          width: max-content;
          max-width: 320px;
          padding: 0.55rem 0.7rem;
          background: var(--theme-surface);
          color: var(--theme-on-surface);
          border: 1px solid var(--theme-primary);
          font-size: 0.75rem;
          font-weight: 400;
          line-height: 1.4;
          text-transform: none;
          letter-spacing: 0;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
          white-space: normal;
          text-align: left;
          opacity: 0;
          pointer-events: none;
          transform: translateY(-4px);
          transition: opacity 0.15s ease, transform 0.15s ease;
          z-index: 1000;
        }

        &::before {
          content: '';
          position: absolute;
          top: calc(100% + 4px);
          left: 6px;
          border: 6px solid transparent;
          border-bottom-color: var(--theme-primary);
          opacity: 0;
          pointer-events: none;
          transform: translateY(-4px);
          transition: opacity 0.15s ease, transform 0.15s ease;
          z-index: 1000;
        }

        &:hover::after,
        &:hover::before {
          opacity: 1;
          transform: translateY(0);
        }

        &.help-icon--up {
          &::after {
            top: auto;
            bottom: calc(100% + 10px);
            transform: translateY(4px);
          }

          &::before {
            top: auto;
            bottom: calc(100% + 4px);
            border-bottom-color: transparent;
            border-top-color: var(--theme-primary);
            transform: translateY(4px);
          }

          &:hover::after,
          &:hover::before {
            transform: translateY(0);
          }
        }
      }
    }

    .card-body {
      padding: 0.6rem 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
    }

    .card-body-flush {
      padding: 0;
    }
  }

  .badge {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    color: white;

    &.badge-running { background: var(--theme-primary); }
    &.badge-idle    { background: var(--theme-on-background-accent); }
    &.badge-total {
      background: transparent;
      color: var(--theme-on-background-accent);
      border: 1px solid var(--theme-highlight);
      text-transform: none;
      letter-spacing: 0;
      font-weight: 600;
    }
  }

  .kv {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.5rem;
    font-size: 0.8rem;

    > span:first-child {
      color: var(--theme-on-background-accent);
    }

    &.kv-error > span:last-child {
      color: var(--theme-error, #c53030);
      word-break: break-word;
      text-align: right;
    }
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.78rem;
    text-align: right;
  }

  .queue-block {
    .queue-title {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--theme-on-background-accent);
      margin-bottom: 0.2rem;
    }

    .queue-counts {
      display: flex;
      flex-wrap: wrap;
      gap: 0.3rem;
    }

    .count {
      display: inline-flex;
      align-items: baseline;
      gap: 0.25rem;
      padding: 0.1rem 0.45rem;
      background: var(--theme-highlight);
      border-radius: 3px;
      font-size: 0.75rem;

      .count-label { color: var(--theme-on-background-accent); }
      .count-value {
        font-weight: 700;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      }
    }

    .breakdown {
      margin: 0.3rem 0 0.5rem;
      padding: 0.35rem 0.5rem;
      background: var(--theme-highlight);
      border-radius: 3px;

      .breakdown-title {
        font-size: 0.65rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--theme-on-background-accent);
        margin-bottom: 0.3rem;
      }

      .breakdown-list {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .breakdown-row {
        display: grid;
        grid-template-columns: minmax(160px, 2fr) 50px 50px minmax(80px, 1.5fr);
        align-items: center;
        gap: 0.4rem;
        font-size: 0.72rem;
        padding: 0.1rem 0;
      }

      .breakdown-mailbox {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .breakdown-count,
      .breakdown-percent {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .breakdown-bar-col {
        display: flex;
        align-items: center;
      }

      .breakdown-bar {
        height: 0.45rem;
        background: var(--theme-primary);
        border-radius: 2px;
        min-width: 2px;
      }
    }
  }

  .sync-list {
    list-style: none;
    margin: 0;
    padding: 0;

    .sync-item {
      display: grid;
      grid-template-columns: minmax(0, 2fr) minmax(56px, 0.55fr) minmax(0, 1fr) minmax(0, 1fr) minmax(64px, 0.65fr) minmax(56px, 0.55fr) minmax(0, 1.5fr);
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 0.75rem;
      transition: background-color 0.15s;

      > * {
        padding: 0.35rem 0.5rem;
        border-left: 1px solid var(--theme-highlight);
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:last-child { border-right: 1px solid var(--theme-highlight); }
      }

      &.header {
        color: var(--theme-on-background-accent);
        font-weight: 600;

        > * { padding: 0.3rem 0.5rem; }
      }

      &:not(.header):hover {
        background-color: var(--theme-highlight);
      }

      &.is-error {
        background-color: rgba(197, 48, 48, 0.06);
      }

      .cell-mailbox {
        font-weight: 600;
      }

      .num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }

      .cell-error {
        color: var(--theme-error, #c53030);
        font-size: 0.72rem;
      }

      .mono {
        white-space: nowrap;
      }
    }
  }

  .dl-list {
    list-style: none;
    margin: 0;
    padding: 0;

    .dl-item {
      display: grid;
      grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.95fr) minmax(0, 0.95fr) minmax(0, 1.5fr) minmax(52px, 0.45fr) minmax(52px, 0.5fr) minmax(0, 1.7fr) minmax(86px, 0.7fr);
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 0.75rem;
      transition: background-color 0.15s;

      > * {
        padding: 0.35rem 0.5rem;
        border-left: 1px solid var(--theme-highlight);
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:last-child { border-right: 1px solid var(--theme-highlight); }
      }

      &.header {
        color: var(--theme-on-background-accent);
        font-weight: 600;

        > * { padding: 0.3rem 0.5rem; }
      }

      &:not(.header):hover {
        background-color: var(--theme-highlight);
      }

      &.is-error {
        background-color: rgba(197, 48, 48, 0.06);
      }

      .cell-mailbox {
        font-weight: 600;
      }

      .cell-subject {
        font-style: italic;
      }

      .num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }

      .cell-error {
        color: var(--theme-error, #c53030);
        font-size: 0.72rem;
      }

      .cell-action {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        padding: 0.15rem 0.25rem;
        white-space: nowrap;
      }

      .mono {
        white-space: nowrap;
      }
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    font-size: 0.8rem;
    color: var(--theme-on-surface);
  }

  .muted {
    color: var(--theme-on-background-accent);
    font-style: italic;
    font-size: 0.8rem;
  }

  .error {
    color: var(--theme-error, #c53030);
    font-size: 0.8rem;
  }

  @media (max-width: 1400px) {
    .sync-list .sync-item,
    .dl-list .dl-item {
      font-size: 0.72rem;
    }
  }

  @media (max-width: 1200px) {
    .sync-list .sync-item {
      grid-template-columns: minmax(0, 1.8fr) minmax(52px, 0.5fr) minmax(0, 0.95fr) minmax(0, 0.95fr) minmax(56px, 0.55fr) minmax(52px, 0.5fr) minmax(0, 1.3fr);
    }

    .dl-list .dl-item {
      grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.85fr) minmax(0, 0.85fr) minmax(0, 1.3fr) minmax(48px, 0.4fr) minmax(48px, 0.45fr) minmax(0, 1.4fr) minmax(80px, 0.65fr);
    }
  }

  .pad { padding: 0.6rem 0.75rem; }

  .hint {
    font-size: 0.72rem;
    color: var(--theme-on-background-accent);
    font-style: italic;
    margin-top: 0.25rem;
  }
}
</style>
